
import Vue from "vue";
import store from "@/store/index";
import {mapState} from "vuex";
import SockJS from "sockjs-client";
import * as StompJs from "@stomp/stompjs";
import {buildContactFullName, Contact, contactApi, EmailAddress} from "@/api/contacts";
import {Notification, notificationApi, NotificationType} from "@/api/notification";
import {commentApi, CommentFor} from "@/api/comment";
import {isDateBeforeMinutes} from "@/utils/DateUtil";
import EventBus from "@/utils/EventBus";
import OmniSearch from "@/layout/OmniSearch.vue";
import {
  AddEvent, AddNaviEvent,
  BackOffNaviEvent,
  BusiNaviEvent,
  ConfigureEmailNaviEvent,
  ConfigureTabNaviEvent,
  LeaveEvent, LoadingStateEvent,
  ResetNaviEvent,
  SaveEvent,
  SaveNaviEvent,
  SelectedContactEvent,
  SendMailToEvent, SnackbarMsgEvent, UploadEvent, UploadNaviEvent
} from "@/layout/Layout.vue";
import {encounterApi} from "@/api/encounter";
import {todoApi, TodoType} from "@/api/todo";
import {flameApi} from "@/api/flame";
import {projectApi} from "@/api/projects";
import {findConfirmDialogRef} from "@/utils/RefUtils";

interface LoginUserEvent {
  username: string;
  lastLogin: string;
}

export interface TabItem {
  title: string;
  tabIndex: number;
  badge?: string;
  alternativeTabValue?: string;
  alternativeEvent?: string;
}

let stompClient: any;

export default Vue.extend({
  name: "App",
  components: {
    OmniSearch,
  },
  data: () => ({
    fab: false,
    loading: false,
    isAdmin: store.state.auth.isAdmin,
    isUser: store.state.auth.isUser,
    reminderProjectsCount: "0",
    reminderTasksCount: "0",
    reminderTodosCount: "0",
    encounterCount: "0",
    contactDuplicates: null as string | null,
    webSocketConnected: false,
    loginUserList: [] as LoginUserEvent[],
    notifications: [] as Notification[],
    incomingCalls: [] as Notification[],
    selectedNotifications: [] as Notification[],
    selectedIncomingCalls: [] as Notification[],
    selectedTodoFinished: [] as Notification[],
    showUploadBtn: false,
    showTimelineBtn: false,
    showBackOffNavi: false,
    currentSaveNaviEvent: {enable: false} as SaveNaviEvent,
    currentAddNaviEvent: {enable: false} as AddNaviEvent,
    currentUploadNaviEvent: {enable: false} as UploadNaviEvent,
    showRecipients: false,
    recipients: [] as EmailAddress[],
    busy: false,
    activeTab: 0,
    tabs: [] as TabItem[],
    currentContact: null as Contact | null,
  }),

  computed: {
    ...mapState("auth", ["currentUser"]),
    ...mapState("navigation", ["pageTitle", "subPageTitle", "latestEntry"]),
  },

  watch: {
    // eslint-disable-next-line
    $route(to, _) {
      store.dispatch("navigation/changePageTitle", to.name);
    },
    activeTab() {
        if (this.tabs && this.tabs.length - 1 >= this.activeTab) {
          EventBus.$emit(this.tabs[this.activeTab].alternativeEvent ?? "changeActiveTabNavi",
              this.tabs[this.activeTab].alternativeTabValue ??  this.tabs[this.activeTab].tabIndex);
        }
      }
  },
  async created() {
    this.setupWebSocket();
  },
  beforeDestroy() {
    stompClient.deactivate();
    EventBus.$off("selectedContact", this.selectedContact);
    EventBus.$off("backOffNavi", this.backOffNavi);
    EventBus.$off("saveNavi", this.saveNavi);
    EventBus.$off("addNavi", this.addNavi);
    EventBus.$off("resetNavi", this.resetNavi);
    EventBus.$off("configureEmailNavi", this.configureEmailNavi);
    EventBus.$off("busiNavi", this.busiNavi);
    EventBus.$off("uploadNavi", this.uploadNavi);
    EventBus.$off("configureTabNavi", this.configureTabNavi);
    EventBus.$off("loading", this.setLoadingState);
  },
  async mounted() {
    EventBus.$on("selectedContact", this.selectedContact);
    EventBus.$on("backOffNavi", this.backOffNavi);
    EventBus.$on("saveNavi", this.saveNavi);
    EventBus.$on("addNavi", this.addNavi);
    EventBus.$on("resetNavi", this.resetNavi);
    EventBus.$on("configureEmailNavi", this.configureEmailNavi);
    EventBus.$on("busiNavi", this.busiNavi);
    EventBus.$on("configureTabNavi", this.configureTabNavi);
    EventBus.$on("loading", this.setLoadingState);
    EventBus.$on("uploadNavi", this.uploadNavi);
  },
  methods: {
    async navigateTo(path: string) {
      if (this.$route.path !== path) {
        await this.$router.push({path: path});
      }
    },
    async logout() {
      await this.$store.dispatch("auth/resetCurrentUser");
      await this.$router.push("/login");
    },
    changePassword() {
      this.$emit("changePassword");
    },
    busiNavi(event: BusiNaviEvent) {
      this.busy = event.enable;
    },
    configureTabNavi(event: ConfigureTabNaviEvent) {
      if (event && event.tabItems.length > 0) {
        this.tabs = event.tabItems.sort((a: TabItem, b: TabItem) => a.tabIndex - b.tabIndex);
        if( event.activeTabByName) {
          this.activeTab = this.tabs.findIndex(value => value.title === event.activeTabByName) ?? 0;
        }
        if( event.activeTab) {
          this.activeTab = event.activeTab
        }
      }
    },
    configureEmailNavi(event: ConfigureEmailNaviEvent) {
      if (event && event.recipients.length > 0) {
        this.recipients = event.recipients;
        this.showRecipients = true;
      } else {
        this.showRecipients = false;
      }
    },
    async resetNavi(event: ResetNaviEvent) {
      if (event && event.enable) {
        this.showBackOffNavi = false;
        this.showTimelineBtn = false;
        this.currentSaveNaviEvent = {enable: false} as SaveNaviEvent;
        this.currentAddNaviEvent = {enable: false} as AddNaviEvent;
        this.showRecipients = false;
        this.recipients = [];
        this.tabs = [];
      }
      this.recipients = [];
      this.activeTab = 0;
    },
    backOffNavi(event: BackOffNaviEvent) {
      if (event && event.enable) {
        this.showBackOffNavi = true;
      } else {
        this.showBackOffNavi = false;
      }
    },
    setLoadingState(event: LoadingStateEvent) {
      this.loading = event.loading;
    },
    saveNavi(event: SaveNaviEvent) {
      this.currentSaveNaviEvent = event;
    },
    uploadNavi(event: UploadNaviEvent) {
      this.currentUploadNaviEvent = event;
    },
    addNavi(event: AddNaviEvent) {
          this.currentAddNaviEvent = event;
    },
    leave() {
      EventBus.$emit("leaveNavi", {enable: true} as LeaveEvent);
      this.recipients = [];
      this.showBackOffNavi = false;
    },
    save() {
      EventBus.$emit("saveContent", {enable: true} as SaveEvent);
    },
    add() {
      EventBus.$emit("addContent", {enable: true} as AddEvent);
    },
    upload() {
      EventBus.$emit("uploadContent", {enable: true} as UploadEvent);
    },
    getTitleForBackButton() {
      if (this.latestEntry && this.latestEntry.indexOf("tasks") > -1) {
        return this.$t("Zurück zur Aufgabe");
      }
      if (this.latestEntry && this.latestEntry.indexOf("emails") > -1) {
        return this.$t("Zurück zur E-Mail");
      }
      return this.$t("Zurück zur Übersicht");
    },
    async selectedContact(event: SelectedContactEvent) {
      if (event.contact && event.contact.id) {
        this.showTimelineBtn = true;
        this.currentContact = event.contact;
        this.encounterCount = "" + (await encounterApi.count(event.contact.id));
        if (this.currentContact.emailAddresses && this.currentContact.emailAddresses.length > 0) {
          this.recipients = this.currentContact.emailAddresses;
          this.showRecipients = true;
        }
      } else {
        this.showTimelineBtn = false;
        this.recipients = [];
      }
    },
    showEncounter() {
      this.$emit("toggleEncounterDialog", this.currentContact);
    },
    async setupWebSocket() {
      this.notifications = [];
      const url =
          window.location.protocol +
          "//" +
          window.location.host +
          "/notifications";

      if (stompClient) {
        await stompClient.deactivate();
        stompClient.forceDisconnect();
      }
      stompClient = new StompJs.Client({
        webSocketFactory: () => new SockJS(url),
      });
      stompClient.heartbeatIncoming = 20000;
      stompClient.heartbeatOutgoing = 20000;
      stompClient.reconnectDelay = 5000;
      stompClient.onConnect = () => {
        stompClient.subscribe(
            "/user/topic/notification/reminder-tasks-event",
            (payload: any) => {
              const notification = JSON.parse(payload.body) as Notification;
              this.reminderTasksCount = notification.content;
            }
        );
        stompClient.subscribe(
            "/user/topic/notification/reminder-projects-event",
            (payload: any) => {
              const notification = JSON.parse(payload.body) as Notification;
              this.reminderProjectsCount = notification.content;
            }
        );
        stompClient.subscribe(
            "/user/topic/notification/complete-todo-event",
            (payload: any) => {
              const todos = JSON.parse(payload.body) as Notification;
              this.reminderTodosCount = todos.content;
            }
        );
        stompClient.subscribe(
            "/user/topic/notification/todo-finished-event",
            (payload: any) => {
              const notification = JSON.parse(payload.body) as Notification;
              const notExist =
                  this.notifications.filter((n) => n.id === notification.id)
                      .length == 0;
              if (notExist) {
                this.notifications.push(notification);
              }
            }
        );
        stompClient.subscribe(
            "/user/topic/notification/contact-duplicates-event",
            (payload: any) => {
              const notification = JSON.parse(payload.body) as Notification;
              notification.content =
                  "Gefundene Duplikate: " + notification.content;
              const notExist =
                  this.notifications.filter((n) => n.id === notification.id)
                      .length == 0;
              if (notExist) {
                this.notifications.push(notification);
              }
            }
        );
        stompClient.subscribe(
            "/user/topic/notification/user-login-event",
            (payload: any) => {
              const notification = JSON.parse(payload.body) as Notification;
              const data = JSON.parse(notification.content);
              this.loginUserList = data.users;
            }
        );
        stompClient.subscribe(
            "/user/topic/notification/comment",
            (payload: any) => {
              const notification = JSON.parse(payload.body) as Notification;
              const notExist =
                  this.notifications.filter((n) => n.id === notification.id)
                      .length == 0;
              if (notExist) {
                this.notifications.push(notification);
              }
            }
        );
        stompClient.subscribe(
            "/user/topic/notification/incoming-call",
            (payload: any) => {
              const notification = JSON.parse(payload.body) as Notification;
              if (notification.createdUtc !== null) {
                notification.createdUtc = new Date(notification.createdUtc);
              }
              const notExist =
                  this.incomingCalls.filter((n) => n.id === notification.id)
                      .length == 0;
              if (notExist) {
                this.incomingCalls.push(notification);
                const isNotToOld = notification.createdUtc
                    ? !isDateBeforeMinutes(notification.createdUtc, 2)
                    : false;
                if (isNotToOld) {
                  this.jumpToIncomingCall(notification);
                }
              }
            }
        );
        stompClient.subscribe(
            "/user/topic/notification/missing-call",
            (payload: any) => {
              const notification = JSON.parse(payload.body) as Notification;
              const notExist =
                  this.notifications.filter((n) => n.id === notification.id)
                      .length == 0;
              if (notExist) {
                this.notifications.push(notification);
              }
            }
        );
        stompClient.subscribe(
            "/user/topic/notification/check-flame",
            (payload: any) => {
              const notification = JSON.parse(payload.body) as Notification;
              const notExist =
                  this.notifications.filter((n) => n.id === notification.id)
                      .length == 0;
              if (notExist) {
                this.notifications.push(notification);
              }
            }
        );
        stompClient.subscribe(
            "/user/topic/notification/check-project",
            (payload: any) => {
              const notification = JSON.parse(payload.body) as Notification;
              const notExist =
                  this.notifications.filter((n) => n.id === notification.id)
                      .length == 0;
              if (notExist) {
                this.notifications.push(notification);
              }
            }
        );
        stompClient.subscribe(
            "/user/topic/notification/interesting-encounter",
            (payload: any) => {
              const notification = JSON.parse(payload.body) as Notification;
              const notExist =
                  this.notifications.filter((n) => n.id === notification.id)
                      .length == 0;
              if (notExist) {
                this.notifications.push(notification);
              }
            }
        );
        stompClient.publish({
          destination: "/swns/start",
          headers: {userName: this.currentUser.username},
        });
      };

      stompClient.onStompError = (frame: any) => {
        EventBus.$emit("snackbarInfosTo", { msg: frame.body} as SnackbarMsgEvent);
      };
      stompClient.activate();

      //15min
      setTimeout(this.setupWebSocket, 900000);
    },
    getCurrentUserAvatarAbbreviation(): string {
      return this.currentUser.username.substr(0, 2);
    },

    async jumpToIncomingCall(notification: Notification) {
      let info = this.$t("Unbekannter Anruf, neuen Kontakt anlegen?") as string;

      if (notification.upstreamRef) {
        const contactName = buildContactFullName(
            await contactApi.getById(notification.upstreamRef),
            null
        );
        info = `Annruf vom Kontakt ${contactName}. Zum Kontakt springen?`;
      }

      const res = await (findConfirmDialogRef(this)).open(
          this.$t("Anruf") as string,
          info,
          {
            textColor: "red",
            textOk: "Ja",
            textCancel: "Nein und zurück",
          }
      );
      if (res) {
        if (notification.upstreamRef) {
          await this.navigateTo(
              `/contacts/${notification.upstreamRef}/encounters/-1`
          );
        } else {
          await this.navigateTo(
              `/contacts/new?phoneNumber=${notification.content.split("(")[0]}`
          );
        }
        await notificationApi.markedAsRead(notification);
      }
    },
    async jumpTo(notification: Notification) {
      if( notification.notificationType == NotificationType.COMMENT) {
        await this.jumpToComment(notification)
      }

      if( notification.notificationType == NotificationType.TODO_FINISHED) {
        await this.jumpToTodoFinished(notification)
      }

      if( notification.notificationType == NotificationType.TASK_REMINDER) {
        await this.jumpToOwnDueTasks()
      }

      if( notification.notificationType == NotificationType.CHECK_FLAME) {
        await this.jumpToFlameTodos(notification)
      }

      if( notification.notificationType == NotificationType.CHECK_PROJECT) {
        await this.jumpToProjectTodos(notification)
      }

      if( notification.notificationType == NotificationType.INTERESTING_ENCOUNTER) {
        await this.jumpToEncounter(notification)
      }
    },
    async jumpToEncounter(notification: Notification) {
      if (notification.upstreamRef != null) {
        const encounter = await encounterApi.getById(notification.upstreamRef);
        if (encounter) {
          EventBus.$emit("resetNavi", {enable: true} as ResetNaviEvent);
          await this.navigateTo(
              `/contacts/${encounter.contactRef}/encounters/${encounter.id}`
          );
        }
      }
    },
    async jumpToComment(notification: Notification) {
      if (notification.upstreamRef != null) {
        const comment = await commentApi.getById(notification.upstreamRef);
        if (comment.commentFor == CommentFor.CONTACT) {
          EventBus.$emit("resetNavi", {enable: true} as ResetNaviEvent);
          await this.navigateTo(
              `/contacts/${comment.upstreamRef}/comments/${comment.id}`
          );
        } else if (comment.commentFor == CommentFor.EMAIL) {
          EventBus.$emit("resetNavi", {enable: true} as ResetNaviEvent);
          await this.navigateTo(
              `/emails/${comment.upstreamRef}/comments/${comment.id}`
          );
        } else if (comment.commentFor == CommentFor.PROJECT) {
          EventBus.$emit("resetNavi", {enable: true} as ResetNaviEvent);
          await this.navigateTo(
              `/projects/${comment.upstreamRef}/comments/${comment.id}`
          );
        }
      }
    },
    async jumpToTodoFinished(notification: Notification) {
      if (notification.upstreamRef != null) {
        const todo = await todoApi.getById(notification.upstreamRef);
        if (todo?.type == TodoType.FLAME) {
          const task = await flameApi.getById(todo.ref);
          EventBus.$emit("resetNavi", {enable: true} as ResetNaviEvent);
          await this.navigateTo(
              `/flames/${todo.ref}/${task?.blackBoardRef}/todos`
          );
        } else if (todo?.type == TodoType.PROJECT) {
          const project = await projectApi.getById(todo.ref);
          EventBus.$emit("resetNavi", {enable: true} as ResetNaviEvent);
          await this.navigateTo(
              `/projects/${todo.ref}/${project?.blackBoardRef}`
          );
        }
      }
    },
    async jumpToFlameTodos(notification: Notification) {
      if (notification.upstreamRef != null) {
        const flame = await flameApi.getById(notification.upstreamRef);
        EventBus.$emit("resetNavi", {enable: true} as ResetNaviEvent);
        await this.navigateTo(
            `/flames/${flame?.id}/${flame?.blackBoardRef}/todos`
        );
      }
    },
    async jumpToProjectTodos(notification: Notification) {
      if (notification.upstreamRef != null) {
        const project = await projectApi.getById(notification.upstreamRef);
        EventBus.$emit("resetNavi", {enable: true} as ResetNaviEvent);
        await this.navigateTo(
            `/projects/${project?.id}/${project?.blackBoardRef}`
        );
      }
    },
    async jumpToFlame(notification: Notification) {
      if (notification.upstreamRef != null) {
        const flame = await flameApi.getById(notification.upstreamRef);
        EventBus.$emit("resetNavi", {enable: true} as ResetNaviEvent);
        await this.navigateTo(
              `/flames/${flame?.id}/${flame?.blackBoardRef}`
          );
      }
    },
    async jumpToOwnDueTasks() {
      await this.navigateTo(`/ownDueTasks`);
    },
    async removeMarkedNotifications() {
      for (const selectedNotification of this.selectedNotifications) {
        const index = this.notifications.findIndex(
            (n) => n.id === selectedNotification.id
        );
        this.notifications.splice(index, 1);
        await notificationApi.markedAsRead(selectedNotification);
      }
      this.selectedNotifications = [];
    },
    async clearAllMarkedNotifications() {
      for (const notifications of this.notifications) {
       await notificationApi.markedAsRead(notifications);
      }
      this.selectedNotifications = [];
      this.notifications = [];
    },
    async removeMarkedIncomingCalls() {
      for (const selectedIncomingCall of this.selectedIncomingCalls) {
        const index = this.incomingCalls.findIndex(
            (n) => n.id === selectedIncomingCall.id
        );
        this.incomingCalls.splice(index, 1);
        await notificationApi.markedAsRead(selectedIncomingCall);
      }
      this.selectedIncomingCalls = [];
    },
    async clearAllMarkedIncomingCalls() {
      for (const incomingCall of this.incomingCalls) {
        await notificationApi.markedAsRead(incomingCall);
      }
      this.selectedIncomingCalls = [];
      this.incomingCalls = []
    },
    toggleDrawer() {
      EventBus.$emit("toggleDrawer");
    },
    sendMailTo(recipient: string | null) {
      EventBus.$emit("sendMailTo", {to: recipient ? [recipient] : null, contact: this.currentContact} as SendMailToEvent);
    },
    async showDashBoard() {
      await this.navigateTo(
          `/dashboards`
      );
    }
  },

});
